/**
 * Serviço de API Centralizado
 * 
 * Centraliza todas as chamadas de API para facilitar manutenção
 * e garantir consistência
 */

import { fetchWithAuth } from '../utils/fetchWithAuth';
import { handleError } from '../utils/errorHandler';
import {
  parseCategoriesResponse,
  normalizeCategoryList,
} from '../utils/categoryUtils.js';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Busca produtos com filtros e paginação
 */
export const getProducts = async (params = {}) => {
  const {
    page = 1,
    limit = 12,
    brand = '',
    category = '',
    minPrice = '',
    maxPrice = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
    tvModel = '',
    boardCode = '',
    search = '',
  } = params;

  try {
    let url = `${API_BASE_URL}/api/products?page=${page}&limit=${limit}`;
    
    if (brand) url += `&brand=${encodeURIComponent(brand)}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    if (minPrice) url += `&minPrice=${minPrice}`;
    if (maxPrice) url += `&maxPrice=${maxPrice}`;
    if (sortBy) url += `&sortBy=${sortBy}`;
    if (sortOrder) url += `&sortOrder=${sortOrder}`;
    if (tvModel) url += `&tvModel=${encodeURIComponent(tvModel)}`;
    if (boardCode) url += `&boardCode=${encodeURIComponent(boardCode)}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Erro ao buscar produtos');
    }

    const data = await response.json();
    return {
      products: data.data?.products || data.products || [],
      pagination: data.data?.pagination || data.pagination,
    };
  } catch (error) {
    handleError(error, { context: 'api.getProducts', showToast: false });
    throw error;
  }
};

/**
 * Busca um produto por ID
 */
export const getProductById = async (productId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/products/${productId}`);
    
    if (!response.ok) {
      throw new Error('Produto não encontrado');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    handleError(error, { context: 'api.getProductById', showToast: false });
    throw error;
  }
};

/**
 * Busca produtos por categoria
 */
export const getProductsByCategory = async (categorySlug) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/products/category/${categorySlug}`
    );
    
    if (!response.ok) {
      throw new Error('Erro ao buscar produtos da categoria');
    }

    const data = await response.json();
    return data.data?.products || data.products || [];
  } catch (error) {
    handleError(error, { context: 'api.getProductsByCategory', showToast: false });
    throw error;
  }
};

/**
 * Busca produtos (search)
 */
export const searchProducts = async (searchTerm) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/products/search?q=${encodeURIComponent(searchTerm)}`
    );
    
    if (!response.ok) {
      throw new Error('Erro ao buscar produtos');
    }

    const data = await response.json();
    return data.data?.products || data.products || data || [];
  } catch (error) {
    handleError(error, { context: 'api.searchProducts', showToast: false });
    throw error;
  }
};

/**
 * Busca sugestões de produtos
 */
export const getProductSuggestions = async (searchTerm, limit = 5) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/products/suggestions?q=${encodeURIComponent(searchTerm)}&limit=${limit}`
    );
    
    if (!response.ok) {
      return { suggestions: [], products: [] };
    }

    const data = await response.json();
    return {
      suggestions: data.data?.suggestions || [],
      products: data.data?.products || [],
    };
  } catch (error) {
    handleError(error, { context: 'api.getProductSuggestions', showToast: false });
    return { suggestions: [], products: [] };
  }
};

/**
 * Busca categorias
 */
export const getCategories = async () => {
  const baseUrl =
    API_BASE_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    'http://localhost:8082';

  if (!baseUrl) {
    throw new Error('URL do backend não configurada (REACT_APP_BACKEND_URL)');
  }

  const response = await fetch(`${baseUrl}/api/categories`);

  if (!response.ok) {
    throw new Error(`Erro ao buscar categorias (${response.status})`);
  }

  const data = await response.json();
  return normalizeCategoryList(parseCategoriesResponse(data));
};

/** getCategories com fallback silencioso (catálogo público) */
export const getCategoriesSafe = async () => {
  try {
    return await getCategories();
  } catch (error) {
    handleError(error, { context: 'api.getCategories', showToast: false });
    return [];
  }
};

/**
 * Verifica estoque de um produto
 */
export const checkProductStock = async (productId, quantity = 1) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/products/check-stock/${productId}?quantity=${quantity}`
    );
    
    if (!response.ok) {
      throw new Error('Erro ao verificar estoque');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    handleError(error, { context: 'api.checkProductStock', showToast: false });
    throw error;
  }
};

/**
 * Calcula frete
 */
export const calculateFreight = async (params) => {
  const {
    cepOrigin = '25260160',
    cepDestination,
    products,
  } = params;

  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/melhorenvio/calculate-freight`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cepOrigin,
          cepDestination: cepDestination.replace(/\D/g, ''),
          products,
        }),
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    handleError(error, { context: 'api.calculateFreight', showToast: false });
    throw error;
  }
};

/**
 * Busca recomendações de produtos
 */
export const getProductRecommendations = async (productId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/products/${productId}/recommendations`
    );
    
    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.recommendations || [];
  } catch (error) {
    handleError(error, { context: 'api.getProductRecommendations', showToast: false });
    return [];
  }
};

